import { CONSTANTS, SHAKE_AMOUNT, BASS_SPECTRUM_OPTIONS } from '../../PreviewDisplayConstants';
import { chooseBackgroundRenderer, chooseImageElementsRenderer, chooseLyricsRenderer, chooseParticlesRenderer, chooseTextRenderer, chooseVisualizerRenderer, chooseYouTubeCtaRenderer, createWaveCircleOptions, getYouTubeCtaPosition, getYouTubeCtaSize, isColorOrGradient } from '../../helpers';

export default class PreviewElementsPipeline {

    constructor(renderer, config) {
        this.renderer = renderer;
        this.stage = renderer.getStage();
        this.width = config.width;
        this.height = config.height;
        this.waveType = config.visualizer.waveType;

        this.backgroundSettingsUpdateTimeStamp = undefined;
        this.visualizerSettingsUpdateTimeStamp = undefined;
        this.visualizerLayersUpdateTimeStamp = undefined;
        this.textSettingsUpdateTimeStamp = undefined;
        this.lyricsSettingsUpdateTimeStamp = undefined;
        this.elementsSettingsUpdateTimeStamp = undefined;

        this.renderElements = {
            background: chooseBackgroundRenderer(config.background.mediaSource, this.stage),
            particles: chooseParticlesRenderer(config.elements.particles.direction, this.stage),
            visualizer: chooseVisualizerRenderer(config.visualizer.shape, this.stage),
            text: chooseTextRenderer(this.stage),
            lyrics: chooseLyricsRenderer(this.stage),
            images: chooseImageElementsRenderer(this.stage),
            youTubeCta: null
        };
    }

    getBackgroundRenderer = () => this.renderElements.background;
    getParticlesRenderer = () => this.renderElements.particles;
    getVisualizerRenderer = () => this.renderElements.visualizer;
    getTextRenderer = () => this.renderElements.text;
    getLyricsRenderer = () => this.renderElements.lyrics;
    getImageElementsRenderer = () => this.renderElements.images;
    getYouTubeCtaRenderer = () => this.renderElements.youTubeCta;

    // The individual elements a canvas gesture can pick up, rather than the containers holding them.
    // Both lists are fixed pools that outlive any one setting, so they are read fresh every time.
    getTextElements = () => this.renderElements.text.textElements;
    getImageElements = () => this.renderElements.images.imageElements;

    updateBackgroundSettings(config, updateTimeStamp) {
        // Replace renderer in the case of new resource type (image, video, etc.)
        const currentMediaResourceType = (this.renderElements.background.mediaSource) ? this.renderElements.background.mediaSource.mediaResourceType : null;
        const newMediaResourceType = (config.background.mediaSource) ? config.background.mediaSource.mediaResourceType : null;

        const rendererNeedsRecreation = currentMediaResourceType !== newMediaResourceType && !(isColorOrGradient(currentMediaResourceType) && isColorOrGradient(newMediaResourceType));

        if (rendererNeedsRecreation) {
            this.renderElements.background.destroy();
            this.renderElements.background = chooseBackgroundRenderer(config.background.mediaSource, this.stage);
        }

        const shakeIntensityMultiplier = (config.background.shakeIntensity ?? 20) / 20;
        const zoomBlurMultiplier = (config.background.motionBlurIntensity ?? 20) / 20;

        this.renderElements.background.update({
            mediaSource: config.background.mediaSource,
            bounceEnabled: config.background.shakeEnabled,
            bounceScale: CONSTANTS.BACKGROUND_BOUNCE_SCALE * shakeIntensityMultiplier,
            shakeEnabled: config.background.shakeEnabled,
            shakeFactor: CONSTANTS.BACKGROUND_SHAKE_FACTOR * shakeIntensityMultiplier,
            paddingFactor: 1 + (CONSTANTS.BACKGROUND_PADDING_FACTOR - 1) * shakeIntensityMultiplier,
            vignetteEnabled: config.background.filterEnabled,
            maxVignette: CONSTANTS.BACKGROUND_MAX_VIGNETTE,
            vignetteFactor: CONSTANTS.BACKGROUND_VIGNETTE_FACTOR,
            contrastEnabled: config.background.filterEnabled,
            maxContrast: CONSTANTS.BACKGROUND_MAX_CONTRAST,
            contrastFactor: CONSTANTS.BACKGROUND_CONTRAST_FACTOR,
            zoomBlurEnabled: config.background.shakeEnabled && (config.background.motionBlurEnabled ?? true),
            maxZoomBlur: CONSTANTS.BACKGROUND_MAX_ZOOM_BLUR * zoomBlurMultiplier,
            zoomBlurFactor: CONSTANTS.BACKGROUND_ZOOM_BLUR_FACTOR * zoomBlurMultiplier,
            spectrumOptions: BASS_SPECTRUM_OPTIONS,
            rotation: config.background.rotation,
            aspectRatio: config.aspectRatio,
            reflectionType: config.background.reflection.type,
            reflectionDirection: config.background.reflection.direction,
            hlsAdjustmentSettings: config.background.hlsAdjustment,
            drift: { ...config.background.drift, coverEdges: true, maxZoom: 1.20 },
            videoAcceleration: config.background.videoAcceleration,
            slowZoom: config.background.slowZoom,
            previewResolution: config.previewResolution,
            onLoadingStateChange: config.onLoadingStateChange,
            onPerformanceFallback: config.onPerformanceFallback
        });

        this.backgroundSettingsUpdateTimeStamp = updateTimeStamp;
    };

    updateVisualizerSettings(config, updateTimeStamp, visualizerLayersUpdateTimeStamp) {
        this.updateVisualizerShape(config.visualizer.shape);

        const waveCircles = config.visualizer.waveCircles;
        const { bassShakeFactor, wideShakeFactor } = this.calculateWaveCircleShakeFactor(config.visualizer.shakeIntensity, config.visualizer.shakeAmount);
        const bassBounceScale = CONSTANTS.BASS_BOUNCE_SCALE * config.visualizer.bounceFactor;
        const wideBounceScale = CONSTANTS.WIDE_BOUNCE_SCALE * config.visualizer.bounceFactor;

        const waveCircleOptions = createWaveCircleOptions(
            config.visualizer.waveType,
            config.visualizer.layoutType,
            waveCircles.length,
            config.visualizer.delayed,
            config.visualizer.seperationFactor,
            config.visualizer.waveScaleFactor,
            config.visualizer.barCount,
            config.visualizer.reflectionType,
            config.visualizer.smoothed,
            config.visualizer.waveStyle,
            config.visualizer.shape,
            config.visualizer.movementDirection,
            waveCircles.map(element => element.customOptions)
        );

        waveCircles.forEach((e, i) => Object.assign(waveCircles[i], waveCircleOptions[i]));

        // Reinitialize layers BEFORE update, so PIXI objects match the new settings array
        if (this.visualizerLayersUpdateTimeStamp !== visualizerLayersUpdateTimeStamp)
            this.renderElements.visualizer.updateLayers(waveCircles);

        this.visualizerLayersUpdateTimeStamp = visualizerLayersUpdateTimeStamp;

        this.renderElements.visualizer.update({
            ...config.visualizer,
            bounceEnabled: config.visualizer.bounceFactor !== 0,
            shakeEnabled: bassShakeFactor !== 0 || wideShakeFactor !== 0,
            bassShakeFactor: bassShakeFactor,
            wideShakeFactor: wideShakeFactor,
            bassBounceScale: bassBounceScale,
            wideBounceScale: wideBounceScale,
            reflection: config.visualizer.reflectionType,
            waveCircles: waveCircles,
            drift: { ...config.visualizer.drift, coverEdges: true, maxZoom: 2.0 }
        });
        this.visualizerSettingsUpdateTimeStamp = updateTimeStamp;
    };

    updateVisualizerShape(shape) {
        // Replace renderer in the case of new shape (circle, flat, etc.)
        if (this.renderElements.visualizer.config.shape !== shape) {
            this.renderElements.visualizer.destroy();
            this.renderElements.visualizer = chooseVisualizerRenderer(shape, this.stage);
        }
    }

    calculateWaveCircleShakeFactor(shakeIntensity, shakeAmount) {
        // Legacy documents stored a discrete shakeAmount; keep honouring them exactly.
        if (shakeIntensity === undefined || shakeIntensity === null) {
            const LEGACY_SHAKE_FACTOR = {
                [SHAKE_AMOUNT.LITTLE]: {
                    bassShakeFactor: CONSTANTS.BASS_SHAKE_FACTOR_LITTLE,
                    wideShakeFactor: CONSTANTS.WIDE_SHAKE_FACTOR_LITTLE,
                },
                [SHAKE_AMOUNT.LOT]: {
                    bassShakeFactor: CONSTANTS.BASS_SHAKE_FACTOR_LOT,
                    wideShakeFactor: CONSTANTS.WIDE_SHAKE_FACTOR_LOT,
                },
                [SHAKE_AMOUNT.NONE]: {
                    bassShakeFactor: 0,
                    wideShakeFactor: 0,
                }
            };

            return LEGACY_SHAKE_FACTOR[shakeAmount] ?? { bassShakeFactor: 0, wideShakeFactor: 0 };
        }

        // Slider is anchored so the default (20) yields a multiplier of 1; 100 reaches 5x.
        const shakeIntensityMultiplier = shakeIntensity / 20;
        return {
            bassShakeFactor: CONSTANTS.BASS_SHAKE_FACTOR_LITTLE * shakeIntensityMultiplier,
            wideShakeFactor: CONSTANTS.WIDE_SHAKE_FACTOR_LITTLE * shakeIntensityMultiplier,
        };
    }

    updateTextSettings(config, updateTimeStamp) {
        this.renderElements.text.update({
            ...config.text
        });

        this.textSettingsUpdateTimeStamp = updateTimeStamp;
    };

    updateLyricsSettings(config, updateTimeStamp) {
        this.renderElements.lyrics.update({
            ...config.lyrics
        });

        this.lyricsSettingsUpdateTimeStamp = updateTimeStamp;
    };

    updateElementsSettings(config, updateTimeStamp, refreshFrame) {
        // Replace particles renderer in the case of new direction (side linear, from center, etc.)
        if (config.elements.particles.direction !== this.renderElements.particles.direction) {
            this.renderElements.particles.destroy();
            this.renderElements.particles = chooseParticlesRenderer(config.elements.particles.direction, this.stage);
        }

        const particleTextures = config.elements.particles.items.map(element => ({
            color: element.color,
            minOpacity: element.minOpacity,
            maxOpacity: element.maxOpacity,
            minSize: element.minSize,
            maxSize: element.maxSize,
            shape: element.shape,
            mediaData: element.mediaData,
            birthRate: element.birthRate,
            flickering: element.flickering,
            rotationEnabled: element.rotationEnabled,
        }));

        this.renderElements.particles.update({
            enabled: config.elements.particles.enabled,
            speedUpEnabled: config.elements.particles.speedUpEnabled,
            direction: config.elements.particles.direction,
            spectrumOptions: BASS_SPECTRUM_OPTIONS,
            particleTextures: particleTextures,
            flock: config.elements.particles.flock,
        });

        // A picked image is still downloading when the settings arrive, so the frame has to be
        // drawn again once its texture is ready or a paused preview would stay empty.
        this.renderElements.images.update({
            imageElements: config.elements.imageElements || config.elements.images || [],
            onTextureLoaded: refreshFrame
        });

        const updateYouTubeCta = (config) => {
            this.renderElements.youTubeCta?.update(
                config.elements.youTubeCta.enabled,
                getYouTubeCtaPosition(config.elements.youTubeCta.cornerPosition, CONSTANTS.DEFAULT_WIDTH, CONSTANTS.DEFAULT_HEIGHT),
                getYouTubeCtaSize(CONSTANTS.DEFAULT_WIDTH, CONSTANTS.DEFAULT_HEIGHT)
            );
        }

        if (!this.renderElements.youTubeCta && config.elements.youTubeCta.enabled) {
            this.renderElements.youTubeCta = chooseYouTubeCtaRenderer(config.elements.youTubeCta, this.stage, () => {
                updateYouTubeCta(config);
                refreshFrame();
            });
        }

        updateYouTubeCta(config);
        this.elementsSettingsUpdateTimeStamp = updateTimeStamp;
    };

    draw(data, bassData, playbackTime, playingPreview, freezeVideoBackground, previewRunning) {
        this.renderElements.background.draw(bassData, freezeVideoBackground, previewRunning);
        this.renderElements.particles.draw(bassData);
        this.renderElements.visualizer.draw(data, bassData, playingPreview);
        this.renderElements.text.draw(bassData);
        this.renderElements.lyrics.draw(playbackTime);
        this.renderElements.youTubeCta?.draw(playingPreview);
    }

    updateRenderer(renderer) {
        this.renderer = renderer;
        this.stage = renderer.getStage();

        this.renderElements.background.updateStage(this.stage);
        this.renderElements.particles.updateStage(this.stage);
        this.renderElements.visualizer.updateStage(this.stage);
        this.renderElements.text.updateStage(this.stage);
        this.renderElements.lyrics.updateStage(this.stage);
        this.renderElements.images.updateStage(this.stage);
        this.renderElements.youTubeCta.updateStage(this.stage);
    }

    destroy() {
        this.renderElements.background.destroy();
        this.renderElements.particles.destroy();
        this.renderElements.visualizer.destroy();
        this.renderElements.text.destroy();
        this.renderElements.lyrics.destroy();
        this.renderElements.images.destroy();
        this.renderElements.youTubeCta?.destroy();
    }
}